import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Play, Pause, SkipBack, SkipForward } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { fetchArticles, fetchCategories, fetchPublishers } from '../services/api';
import { useToast } from '../hooks/use-toast';
import BottomNav from '../components/BottomNav';
import { useTheme } from '../context/ThemeContext';
import { App as CapacitorApp } from '@capacitor/app';
import { 
  getCachedCategories, setCachedCategories,
  getCachedPublishers, setCachedPublishers,
  getCachedArticles, setCachedArticles,
  generateFilterKey
} from '../utils/dataCache';
import { trackCategorySelect, trackPublisherSelect, trackAudioPlay } from '../services/analytics';

const DriveMode = () => {
  const { darkMode } = useTheme();
  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [publishers, setPublishers] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedPublisher, setSelectedPublisher] = useState('all');
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [activeTab, setActiveTab] = useState('summary'); // 'summary' or 'full'
  const [audioUrl, setAudioUrl] = useState(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isAudioLoading, setIsAudioLoading] = useState(false);
  
  const audioRef = useRef(null);
  const { toast } = useToast();

  // Format date to "2nd Jan'26" format
  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      const dateParts = dateString.split('T')[0].split('-');
      const year = dateParts[0];
      const month = parseInt(dateParts[1]) - 1;
      const day = parseInt(dateParts[2]);
      
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const monthName = monthNames[month];
      const yearShort = year.slice(-2);
      
      const getOrdinal = (n) => {
        const s = ['th', 'st', 'nd', 'rd'];
        const v = n % 100;
        return n + (s[(v - 20) % 10] || s[v] || s[0]);
      };
      
      return `${getOrdinal(day)} ${monthName}'${yearShort}`;
    } catch (error) {
      return '';
    }
  };

  // Handle Android back button
  useEffect(() => {
    let backButtonListener;
    
    const setupBackButton = async () => {
      backButtonListener = await CapacitorApp.addListener('backButton', () => {
        if (isPlaying && audioRef.current) {
          audioRef.current.pause();
          setIsPlaying(false);
        }
        window.location.href = '/';
      });
    };
    
    setupBackButton();

    return () => {
      if (backButtonListener) {
        backButtonListener.remove();
      }
    };
  }, [isPlaying]);


  // Load categories and publishers - use cache first
  useEffect(() => {
    const loadInitialData = async () => {
      // Try to load from cache first
      const cachedCategories = getCachedCategories();
      const cachedPublishers = getCachedPublishers();
      
      if (cachedCategories && cachedCategories.length > 0) {
        setCategories(cachedCategories);
      }
      if (cachedPublishers && cachedPublishers.length > 0) {
        setPublishers(cachedPublishers);
      }

      // Refresh from backend in background
      try {
        const [categoriesData, publishersData] = await Promise.all([
          fetchCategories(),
          fetchPublishers()
        ]);
        setCategories(categoriesData);
        setPublishers(publishersData);
        setCachedCategories(categoriesData);
        setCachedPublishers(publishersData);
      } catch (error) {
        console.error('Error loading initial data:', error);
        // Keep using cached data on error
      }
    };
    loadInitialData();
  }, []);

  // Load articles based on filters - FIX: prevent count flicker and cycling during playback
  useEffect(() => {
    const loadArticles = async () => {
      const categoryFilter = selectedCategory === 'all' ? null : categories.find(c => c.id === selectedCategory)?.name;
      const publisherFilter = selectedPublisher === 'all' ? null : publishers.find(p => p.id === selectedPublisher)?.name;
      
      const filterKey = generateFilterKey(categoryFilter, publisherFilter);
      
      // FIX 1: Don't show cached count - removes flicker
      // Removed: if (cachedArticles) { setArticles(cachedArticles); }
      
      // Always fetch fresh data from backend
      try {
        const data = await fetchArticles(categoryFilter, publisherFilter, 100);
        
        // FIX 2: Only update articles if NOT playing (prevents cycling)
        if (!isPlaying) {
          setArticles(data.articles);
          setCurrentIndex(0);
        }
        
        // Always update cache (even if playing)
        setCachedArticles(filterKey, data.articles);
      } catch (error) {
        console.error('Error loading articles:', error);
      }
    };
    
    if (categories.length > 0 && publishers.length > 0) {
      loadArticles();
    }
  }, [selectedCategory, selectedPublisher, categories, publishers, isPlaying]);

  const currentArticle = articles[currentIndex];

  // Fetch audio URL when article or tab changes
  useEffect(() => {
    const fetchAudioUrl = async () => {
      if (!currentArticle) return;
      
      setIsAudioLoading(true);
      setAudioUrl(null); // Clear previous audio
      
      try {
        const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
        const audioType = activeTab === 'summary' ? 'summary' : 'full';
        const response = await fetch(`${BACKEND_URL}/api/audio/${currentArticle.id}/${audioType}`);
        const data = await response.json();
        
        console.log('Fetched audio URL:', data);
        
        if (data.url) {
          setAudioUrl(data.url);
        } else {
          console.error('No audio URL found');
          toast({
            title: 'Audio Not Available',
            description: 'Audio for this article is not available. Skipping...',
          });
          // Skip to next if no audio
          if (currentIndex < articles.length - 1) {
            setCurrentIndex(prev => prev + 1);
          }
        }
      } catch (error) {
        console.error('Error fetching audio URL:', error);
        toast({
          title: 'Error Loading Audio',
          description: 'Could not load audio. Skipping to next article...',
        });
        // Skip to next on error
        if (currentIndex < articles.length - 1) {
          setCurrentIndex(prev => prev + 1);
        }
      } finally {
        setIsAudioLoading(false);
      }
    };

    fetchAudioUrl();
  }, [currentArticle, activeTab, currentIndex, articles.length, toast]);

  // Don't auto-pause voice recognition - keep it active for hands-free control
  // Instead, we'll use higher confidence thresholds during playback
  useEffect(() => {
    if (!audioUrl || !audioRef.current) return;

    console.log('Audio URL loaded:', audioUrl);
    
    // Load the audio
    audioRef.current.load();
    
    // Only play if isPlaying is true
    if (isPlaying) {
      const playPromise = audioRef.current.play();
      
      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            console.log('Audio playing successfully');
          })
          .catch((error) => {
            console.log('Auto-play prevented:', error);
            setIsPlaying(false);
          });
      }
    }
  }, [audioUrl]); // Only depend on audioUrl, not isPlaying

  // Handle play/pause based on isPlaying state
  useEffect(() => {
    if (!audioRef.current || !audioUrl) return;

    const audio = audioRef.current;

    if (isPlaying) {
      // Wait for audio to be ready before playing
      const attemptPlay = () => {
        if (audio.readyState >= 2) { // HAVE_CURRENT_DATA or better
          console.log('Audio ready, playing...');
          const playPromise = audio.play();
          if (playPromise !== undefined) {
            playPromise.catch((error) => {
              console.log('Play error:', error);
              setIsPlaying(false);
            });
          }
        } else {
          console.log('Audio not ready, waiting... readyState:', audio.readyState);
          // Wait for canplay event
          audio.addEventListener('canplay', attemptPlay, { once: true });
          setTimeout(() => {
            if (audio.readyState < 2) {
              console.log('Audio taking too long to load');
              setIsAudioLoading(false);
            }
          }, 5000);
        }
      };
      
      attemptPlay();
    } else {
      audio.pause();
    }
  }, [isPlaying, audioUrl]);

  // Handle audio end - play next article
  const handleAudioEnd = useCallback(() => {
    if (currentIndex < articles.length - 1) {
      setCurrentIndex(prev => prev + 1);
      setCurrentTime(0);
      // Keep playing
    } else {
      setIsPlaying(false);
      toast({
        title: 'Playlist Complete',
        description: 'All articles have been played.',
      });
    }
  }, [currentIndex, articles.length, toast]);

  const handlePlayPause = () => {
    if (isPlaying) {
      audioRef.current?.pause();
    } else {
      audioRef.current?.play();
    }
    setIsPlaying(!isPlaying);
  };

  const handleNext = () => {
    if (currentIndex < articles.length - 1) {
      setCurrentIndex(prev => prev + 1);
      setCurrentTime(0);
    }
  };

  const handlePrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(prev => prev - 1);
      setCurrentTime(0);
    }
  };

  // Voice Recognition Setup removed

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-800 text-white pb-20">
      {/* Safe area padding for mobile status bar */}
      <div className="pt-[env(safe-area-inset-top,12px)]" />
      
      {/* Header Message */}
      <div className="bg-red-600 px-4 py-2 flex items-center gap-2">
        <span className="text-2xl">🚗</span>
        <span className="text-sm font-medium">Drive Safely, keep your eyes on the road</span>
      </div>

      {/* Filters */}
      <div className="px-4 py-4 flex gap-3">
        <Select value={selectedPublisher} onValueChange={setSelectedPublisher}>
          <SelectTrigger className="flex-1 bg-slate-700 border-slate-600 text-white">
            <SelectValue placeholder="All Sources" />
          </SelectTrigger>
          <SelectContent>
            {publishers.map((publisher) => (
              <SelectItem key={publisher.id} value={publisher.id}>
                {publisher.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={selectedCategory} onValueChange={setSelectedCategory}>
          <SelectTrigger className="flex-1 bg-slate-700 border-slate-600 text-white">
            <SelectValue placeholder="All Categories" />
          </SelectTrigger>
          <SelectContent>
            {categories.map((category) => (
              <SelectItem key={category.id} value={category.id}>
                {category.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Article Display */}
      {currentArticle && (
        <div className="px-4 py-6">
          {/* Counter */}
          <div className="text-lg mb-4">
            {currentIndex + 1} of {articles.length}
          </div>

          {/* Article Info Card */}
          <Card className="bg-slate-800 border-slate-700 p-6 mb-6">
            {/* Duration Badge */}
            <div className="flex justify-end mb-3">
              <span className="bg-yellow-500 text-slate-900 px-3 py-1 rounded-full text-sm font-bold flex items-center gap-1">
                <span>⚡</span>
                <span>{activeTab === 'summary' ? 'SUMMARY' : 'FULL'}</span>
              </span>
            </div>

            {/* Headline */}
            <h1 className="text-3xl font-bold mb-4 text-center text-white">
              {currentArticle.title}
            </h1>

            {/* Source */}
            <p className="text-center text-slate-400 mb-6">
              {currentArticle.source} - {formatDate(currentArticle.published_at)} - {currentArticle.category}
            </p>

            {/* Tabs */}
            <div className="flex gap-2 mb-6">
              <Button
                onClick={() => setActiveTab('summary')}
                className={`flex-1 py-6 text-lg ${
                  activeTab === 'summary'
                    ? 'bg-blue-600 hover:bg-blue-700'
                    : 'bg-slate-700 hover:bg-slate-600'
                }`}
              >
                Summary
              </Button>
              <Button
                onClick={() => setActiveTab('full')}
                className={`flex-1 py-6 text-lg ${
                  activeTab === 'full'
                    ? 'bg-blue-600 hover:bg-blue-700'
                    : 'bg-slate-700 hover:bg-slate-600'
                }`}
              >
                Full Article
              </Button>
            </div>

            {/* Audio Controls */}
            <div className="flex items-center justify-center gap-8 mb-4">
              <Button
                onClick={handlePrevious}
                disabled={currentIndex === 0}
                size="icon"
                className="h-16 w-16 rounded-full bg-slate-700 hover:bg-slate-600 disabled:opacity-30"
              >
                <SkipBack className="w-8 h-8" />
              </Button>

              <Button
                onClick={handlePlayPause}
                size="icon"
                className="h-24 w-24 rounded-full bg-blue-600 hover:bg-blue-700"
              >
                {isPlaying ? (
                  <Pause className="w-16 h-16" fill="currentColor" />
                ) : (
                  <Play className="w-16 h-16 ml-1" fill="currentColor" />
                )}
              </Button>

              <Button
                onClick={handleNext}
                disabled={currentIndex === articles.length - 1}
                size="icon"
                className="h-16 w-16 rounded-full bg-slate-700 hover:bg-slate-600 disabled:opacity-30"
              >
                <SkipForward className="w-8 h-8" />
              </Button>
            </div>

            {/* Duration */}
            <div className="text-center text-slate-400">
              {isAudioLoading ? (
                <p className="text-yellow-400">⏳ Loading audio...</p>
              ) : (
                <p>{formatTime(currentTime)}</p>
              )}
            </div>
          </Card>

          {/* Instructions */}
          <div className="mt-4 text-center text-sm text-slate-400">
            <p className="flex items-center justify-center gap-2">
              <span>🚗</span>
              Large controls optimized for easy access while driving
            </p>
          </div>
        </div>
      )}

      {!currentArticle && articles.length === 0 && (
        <div className="text-center py-12 text-slate-400">
          <p>Loading articles...</p>
        </div>
      )}

      {/* Hidden Audio Element */}
      <audio
        ref={audioRef}
        src={audioUrl || ''}
        preload="auto"
        onTimeUpdate={(e) => {
          setCurrentTime(e.target.currentTime);
        }}
        onLoadedMetadata={(e) => {
          console.log('Audio metadata loaded, duration:', e.target.duration);
          setDuration(e.target.duration);
          setIsAudioLoading(false);
        }}
        onEnded={handleAudioEnd}
        onPlay={() => {
          console.log('Audio started playing');
          setIsPlaying(true);
        }}
        onPause={() => {
          console.log('Audio paused');
        }}
        onError={(e) => {
          console.error('Audio error:', e.target.error);
          setIsAudioLoading(false);
          toast({
            title: 'Audio Error',
            description: 'Could not play this article. Skipping to next...',
            variant: 'destructive'
          });
          // Skip to next article on error
          setTimeout(() => {
            if (currentIndex < articles.length - 1) {
              setCurrentIndex(prev => prev + 1);
            } else {
              setIsPlaying(false);
            }
          }, 1000);
        }}
        onLoadStart={() => {
          console.log('Audio loading started');
          setIsAudioLoading(true);
        }}
        onCanPlay={() => {
          console.log('Audio can play');
          setIsAudioLoading(false);
        }}
        onWaiting={() => console.log('Audio waiting/buffering')}
        onStalled={() => console.log('Audio stalled')}
      />

      {/* Bottom Navigation */}
      <BottomNav activeTab="drive" darkMode={true} />
    </div>
  );
};

export default DriveMode;